import "@fontsource/caveat/400.css";
import type { PdfBaustein } from "../../../services/pdfBausteinService";

/**
 * Baustein (Unterschrift/Stempel) → fertiges PNG, das als PDF.js-Stempel ins PDF wandert.
 *
 * Zweigeteilt: `berechneLayout` rechnet rein (wo steht was, testbar ohne Canvas),
 * `bausteinAlsPng` malt das Ergebnis. Alle Maße in CSS-Pixeln; gemalt wird in dreifacher
 * Auflösung, damit die Unterschrift im PDF auch beim Hineinzoomen scharf bleibt.
 *
 * Layout-Regeln:
 * - Unterschrift: Bild oben, darunter die Textzeilen linksbündig (14 px, bei „Handschrift“
 *   Caveat 20 px), darunter das Datum.
 * - Bild-Stempel: Bild, Datum mittig darunter in der Stempelfarbe.
 * - Text-Stempel: abgerundeter Rahmen (2 px, Stempelfarbe), Zeilen zentriert, fett, 18 px,
 *   in Großbuchstaben; Datum 14 px darunter.
 */

export interface BildLayout {
    breite: number;
    hoehe: number;
    bild?: { x: number; y: number; w: number; h: number };
    /** `y` = Oberkante der Zeile (gezeichnet mit `textBaseline = "top"`). */
    zeilen: { text: string; x: number; y: number; groesse: number; schrift: string }[];
    rahmen?: { x: number; y: number; w: number; h: number; radius: number };
}

export type TextMasse = (text: string, px: number, schrift: string) => number;

const INNEN = 8;
/** Text-Stempel: zusätzlicher Luftraum zwischen Rahmen und Schrift. */
const RAHMEN_LUFT = 4;
const RAHMEN_STRICH = 2;
const ZEILENHOEHE = 1.3;
const ABSTAND_BILD_TEXT = 4;
const AUFLOESUNG = 3;

export const SCHRIFT_NORMAL = (px: number) => `${px}px "Segoe UI", Arial, sans-serif`;
export const SCHRIFT_HANDSCHRIFT = (px: number) => `${px}px "Caveat", cursive`;
export const SCHRIFT_STEMPEL = (px: number) => `bold ${px}px "Segoe UI", Arial, sans-serif`;

export function datumText(d: Date): string {
    const zz = (n: number) => String(n).padStart(2, "0");
    return `${zz(d.getDate())}.${zz(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function textZeilen(text: string): string[] {
    return text
        .split("\n")
        .map((z) => z.trim())
        .filter(Boolean);
}

/** Reine Rechnung (testbar ohne Canvas): wo steht was. `masse` misst Textbreiten. */
export function berechneLayout(
    b: PdfBaustein,
    bildGroesse: { w: number; h: number } | null,
    heute: Date,
    masse: TextMasse,
): BildLayout {
    const textStempel = b.art === "stempel" && !bildGroesse;
    const zentriert = b.art === "stempel";
    const rand = textStempel ? INNEN + RAHMEN_LUFT : INNEN;

    type Roh = { text: string; groesse: number; schrift: string };
    const roh: Roh[] = [];
    if (textStempel) {
        for (const z of textZeilen(b.text)) roh.push({ text: z.toUpperCase(), groesse: 18, schrift: SCHRIFT_STEMPEL(18) });
    } else if (b.art === "unterschrift") {
        for (const z of textZeilen(b.text)) {
            roh.push(
                b.handschrift
                    ? { text: z, groesse: 20, schrift: SCHRIFT_HANDSCHRIFT(20) }
                    : { text: z, groesse: 14, schrift: SCHRIFT_NORMAL(14) },
            );
        }
    }
    if (b.mitDatum) roh.push({ text: datumText(heute), groesse: 14, schrift: SCHRIFT_NORMAL(14) });

    const breiten = roh.map((z) => masse(z.text, z.groesse, z.schrift));
    const inhaltBreite = Math.max(bildGroesse?.w ?? 0, ...breiten, 0);
    const breite = inhaltBreite + 2 * rand;

    let y = rand;
    let bild: BildLayout["bild"];
    if (bildGroesse) {
        const x = zentriert ? (breite - bildGroesse.w) / 2 : rand;
        bild = { x, y, w: bildGroesse.w, h: bildGroesse.h };
        y += bildGroesse.h;
        if (roh.length) y += ABSTAND_BILD_TEXT;
    }
    const zeilen = roh.map((z, i) => {
        const zeile = { ...z, x: zentriert ? (breite - breiten[i]) / 2 : rand, y };
        y += z.groesse * ZEILENHOEHE;
        return zeile;
    });
    const hoehe = y + rand;

    const layout: BildLayout = { breite, hoehe, zeilen };
    if (bild) layout.bild = bild;
    if (textStempel) {
        const h = RAHMEN_STRICH / 2;
        layout.rahmen = { x: h, y: h, w: breite - RAHMEN_STRICH, h: hoehe - RAHMEN_STRICH, radius: 6 };
    }
    return layout;
}

function ladeBild(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Das Bild der Unterschrift/des Stempels ließ sich nicht laden."));
        img.src = dataUrl;
    });
}

/** Unterschrift-Bilder werden auf diese Breite (CSS-px) gebracht, damit Text daneben passt. */
const BILD_ZIELBREITE = 300;

/** Zeichnet das Layout auf ein Canvas und liefert PNG (transparenter Hintergrund, 3× Auflösung). */
export async function bausteinAlsPng(b: PdfBaustein, heute: Date = new Date()): Promise<Blob> {
    const img = b.bildDataUrl ? await ladeBild(b.bildDataUrl) : null;
    if (b.handschrift) {
        try {
            await document.fonts.load(SCHRIFT_HANDSCHRIFT(20));
        } catch {
            /* ohne Schrift zeichnet der Browser die Ausweichschrift */
        }
    }
    let bildGroesse: { w: number; h: number } | null = null;
    if (img && img.naturalWidth > 0) {
        const f = BILD_ZIELBREITE / img.naturalWidth;
        bildGroesse = { w: BILD_ZIELBREITE, h: Math.max(1, Math.round(img.naturalHeight * f)) };
    }

    const messCanvas = document.createElement("canvas");
    const mess = messCanvas.getContext("2d");
    if (!mess) throw new Error("Canvas wird von diesem Browser nicht unterstützt.");
    const masse: TextMasse = (text, _px, schrift) => {
        mess.font = schrift;
        return mess.measureText(text).width;
    };
    const l = berechneLayout(b, bildGroesse, heute, masse);

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(l.breite * AUFLOESUNG);
    canvas.height = Math.ceil(l.hoehe * AUFLOESUNG);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas wird von diesem Browser nicht unterstützt.");
    ctx.scale(AUFLOESUNG, AUFLOESUNG);

    if (img && l.bild) ctx.drawImage(img, l.bild.x, l.bild.y, l.bild.w, l.bild.h);
    ctx.fillStyle = b.farbe;
    ctx.textBaseline = "top";
    for (const z of l.zeilen) {
        ctx.font = z.schrift;
        ctx.fillText(z.text, z.x, z.y);
    }
    if (l.rahmen) {
        const r = l.rahmen;
        ctx.strokeStyle = b.farbe;
        ctx.lineWidth = RAHMEN_STRICH;
        ctx.beginPath();
        ctx.roundRect(r.x, r.y, r.w, r.h, r.radius);
        ctx.stroke();
    }

    return new Promise((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("PNG konnte nicht erzeugt werden."))), "image/png"),
    );
}
